import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft, Lock } from "lucide-react";
import { login, setSession } from "@/lib/apiClient";

export default function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      toast.error("Enter your username and password.");
      return;
    }
    setSubmitting(true);
    try {
      const { token, username: u } = await login(username.trim(), password);
      setSession(token, u);
      toast.success(`Welcome, ${u}.`);
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="login-page" className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <Link to="/" data-testid="login-back-home" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-white/60 hover:text-white transition-colors">
            <ArrowLeft size={14} /> Back home
          </Link>
          <span className="font-display text-lg font-black tracking-tighter uppercase">
            Vogue<span className="text-white/70">Verve</span>
          </span>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center mb-6">
            <Lock size={18} strokeWidth={1.6} />
          </div>
          <p className="text-[10px] uppercase tracking-[0.35em] font-bold text-white/40 mb-3">
            Scouting Console
          </p>
          <h1 className="font-display text-5xl font-light tracking-tight leading-[0.95] mb-3">
            Sign in.
          </h1>
          <p className="text-sm text-white/50 leading-relaxed mb-10">
            This dashboard is private. Enter the admin credentials to review applicants.
          </p>

          <form onSubmit={submit} className="space-y-6" data-testid="login-form">
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 block mb-2">
                Username
              </label>
              <input
                data-testid="login-username"
                autoFocus
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="helloadmin…"
                className="w-full bg-transparent border-0 border-b border-white/25 text-lg text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 block mb-2">
                Password
              </label>
              <input
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-transparent border-0 border-b border-white/25 text-lg text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3"
              />
            </div>

            <button
              data-testid="login-submit"
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-between rounded-full bg-white text-black px-7 py-4 text-xs uppercase tracking-[0.25em] font-bold hover:bg-white/85 transition-colors disabled:opacity-60"
            >
              {submitting ? (
                <><Loader2 size={16} className="animate-spin" /> Signing in…<span /></>
              ) : (
                <>Sign in <ArrowRight size={16} /></>
              )}
            </button>
          </form>

          <p className="text-[10px] uppercase tracking-[0.25em] text-white/30 mt-8 leading-relaxed">
            Session expires after 24 hours. Contact the owner for access.
          </p>
        </div>
      </div>
    </main>
  );
}
